import AppDialog from '../../components/app_dialog'
import RowMenu from '../../components/row_menu'
import { Button, Menu, TextField, TextArea, EmptyState } from '@cinesend/ui-kit'
import DataTable from '../../components/data_table'
import { Plus } from '@phosphor-icons/react'
import { useQuery } from '@tanstack/react-query'
import useStore from '../../hooks/use_store'
import { useState } from 'react'

const ImageSpecs = ({ airline }) => {
  const { data: imageSpecs, error, isLoading, refetch } = useQuery(['airline-image-specs', `?airline_id=${airline.id}`])
  const { createModel, updateModel, deleteModel } = useStore()
  const [showEditModalForSpec, setShowEditModalForSpec] = useState(null)

  const AIRLINE_IMAGE_SPEC_TYPE = [
    'seriesPoster',
    'seriesHero',
    'seriesFeatureNarrow',
    'seriesFeatureWide',
    'episodePoster',
    'moviePoster',
    'movieHero',
    'movieFeatureNarrow',
    'movieFeatureWide',
    'audioCover',
  ]

  const createImageSpec = (type) => {
    createModel.mutate({
      endpoint: 'airline-image-specs',
      data: {
        airline_id: airline.id,
        type,
      },
    })
  }

  const updateImageSpec = (spec) => {
    updateModel.mutate(
      {
        endpoint: `airline-image-specs`,
        id: spec.id,
        data: {
          width: spec.width,
          height: spec.height,
          thumbnail_spec: spec.thumbnail_spec,
          variant_name: spec.variant_name,
        },
      },
      {
        onSuccess: () => setShowEditModalForSpec(null),
      }
    )
  }

  const deleteImageSpec = (spec) => {
    deleteModel.mutate({
      endpoint: 'airline-image-specs',
      id: spec.id,
      deleteNoun: 'image spec',
      deleteName: spec.variant_name || spec.type,
    })
  }

  const existingTypes = imageSpecs?.map((spec) => spec.type) ?? []

  const columns = [
    { key: 'type', header: 'Type', render: (data) => data.type },
    { key: 'width', header: 'Width', render: (data) => data.width },
    { key: 'height', header: 'Height', render: (data) => data.height },
    { key: 'variant_name', header: 'Variant Name', render: (data) => data.variant_name },
    {
      key: 'actions',
      header: '',
      width: '55px',
      render: (data) => (
        <RowMenu
          label={`Actions for ${data.type}`}
          items={[
            {
              id: 'edit',

              label: 'Edit',
              onSelect: () => {
                setShowEditModalForSpec(data)
              },
            },
            {
              id: 'delete',

              label: 'Delete',
              tone: 'danger',
              onSelect: () => {
                deleteImageSpec(data)
              },
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <DataTable
        caption='Image specs'
        columns={columns}
        rows={imageSpecs ?? []}
        onRowClick={(data) => setShowEditModalForSpec(data)}
        isLoading={isLoading}
        error={error}
        onRetry={refetch}
        empty={<EmptyState title='No image specs!' detail='Add one to begin.' />}
        actions={
          AIRLINE_IMAGE_SPEC_TYPE.length !== existingTypes.length ? (
            <Menu
              trigger={<Button leadingIcon={<Plus size={20} />}>Add Image Spec</Button>}
              items={AIRLINE_IMAGE_SPEC_TYPE.filter((type) => !existingTypes.includes(type)).map((type) => ({
                id: type,
                label: type,
                onSelect: () => createImageSpec(type),
              }))}
            />
          ) : (
            <Button disabled leadingIcon={<Plus size={20} />}>
              Add Image Spec
            </Button>
          )
        }
      />
      {showEditModalForSpec && (
        <AppDialog
          title='Edit image spec'
          onClose={() => setShowEditModalForSpec(null)}
          confirmText='Save'
          onConfirm={() => {
            updateImageSpec(showEditModalForSpec)
          }}
        >
          <div className='space-y-4'>
            <TextField label={'Type'} value={showEditModalForSpec.type} disabled={true} />
            <TextField
              label={'Variant Name'}
              value={showEditModalForSpec.variant_name ?? ''}
              onChange={(e) =>
                setShowEditModalForSpec({
                  ...showEditModalForSpec,
                  variant_name: e.target.value,
                })
              }
            />
            <div className='flex flex-row space-x-4'>
              <TextField
                label={'Width'}
                type={'number'}
                value={showEditModalForSpec.width ?? 0}
                onChange={(e) =>
                  setShowEditModalForSpec({
                    ...showEditModalForSpec,
                    width: e.target.value,
                  })
                }
              />
              <TextField
                label={'Height'}
                type={'number'}
                value={showEditModalForSpec.height ?? 0}
                onChange={(e) =>
                  setShowEditModalForSpec({
                    ...showEditModalForSpec,
                    height: e.target.value,
                  })
                }
              />
            </div>
            <TextArea
              label={'Thumbnail specs (variant,width,height)'}
              value={showEditModalForSpec.thumbnail_spec ?? ''}
              onChange={(e) =>
                setShowEditModalForSpec({
                  ...showEditModalForSpec,
                  thumbnail_spec: e.target.value,
                })
              }
            />
          </div>
        </AppDialog>
      )}
    </>
  )
}

export default ImageSpecs
